import React, { useState } from "react";
import { useProject } from "../../state/ProjectContext";
import { toast } from "../../lib/utils";
import { api } from "../../lib/api";
import EditableTable from "./_EditableTable";
import { Send, Loader2, Zap, MessageSquare, ShieldAlert, AlertTriangle, Clock, TestTube2 } from "lucide-react";

/**
 * Integrations tab — Teams webhook + 3-tier SLA escalation rules +
 * @mention routing tied to the RACI matrix.
 *
 * Data model (persisted at doc.integrations):
 *   teams: { steeringWebhook, cutoverWebhook, standupWebhook, enabled, slashHint }
 *   escalation: {
 *     tier1DaysBeforeDue, tier2DaysAfterDue, tier3DaysAfterDue,
 *     severityAutoEscalate: bool,   // Critical → straight to Tier 3
 *     onCall: [{ workstream, primary, backup, manager }]
 *   }
 *   log: [{ at, tier, kind, ref, owner, message, sent }]
 */
export default function Integrations() {
  const { doc, mutate, readOnly } = useProject();
  const int = doc?.integrations || {};
  const teams = int.teams || {};
  const esc = int.escalation || {};
  const log = int.log || [];
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);

  const setTeams = (patch) => mutate(d => ({ ...d, integrations: { ...(d.integrations || {}), teams: { ...((d.integrations && d.integrations.teams) || {}), ...patch } } }));
  const setEsc = (patch) => mutate(d => ({ ...d, integrations: { ...(d.integrations || {}), escalation: { ...((d.integrations && d.integrations.escalation) || {}), ...patch } } }));

  // Client-side scanner — walks RAID + actions + testing.defects + cutover.hypercare
  // + tasks and returns a preview of what would be sent.
  const scan = async () => {
    setPreviewing(true);
    try {
      const r = await api.notificationsScan(doc.id);
      setPreview(r);
      toast(`Scan complete · ${r.tier1.length + r.tier2.length + r.tier3.length} items`);
    } catch (e) {
      toast("Scan failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPreviewing(false);
    }
  };

  const sendTest = async () => {
    if (!teams.steeringWebhook) return toast("Add a Teams webhook first");
    setSendingTest(true);
    try {
      await api.notificationsTest(doc.id);
      toast("Test message posted to Teams");
    } catch (e) {
      toast("Test failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSendingTest(false);
    }
  };

  const dispatch = async () => {
    if (!preview) return toast("Run a scan first");
    if (!window.confirm(`Send ${preview.tier1.length} reminders, ${preview.tier2.length} overdue alerts, and ${preview.tier3.length} escalations to Teams?`)) return;
    try {
      const r = await api.notificationsDispatch(doc.id);
      toast(`Dispatched ${r.sent} messages · ${r.failed} failed`);
      setPreview(null);
    } catch (e) {
      toast("Dispatch failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <>
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Notifications &amp; Escalation</div>
        <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Integrations</h2>
        <p className="text-sm text-zinc-500 mt-1 max-w-3xl">Automated status reporting &amp; 3-tier SLA escalation to Microsoft Teams. Threshold alerts, @mention routing via RACI, and slash-command hints.</p>
      </div>

      {/* Teams webhooks */}
      <div className="card p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare size={14} className="text-blue-600"/>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Microsoft Teams · Incoming webhook URLs</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { key: "steeringWebhook", label: "Steering Committee channel",    hint: "Weekly status + Tier-3 escalations" },
            { key: "cutoverWebhook",  label: "Cutover war-room channel",      hint: "Go-live weekend live feed" },
            { key: "standupWebhook",  label: "Delivery standup channel",      hint: "Daily digest for Realize / Deploy" },
          ].map(f => (
            <label key={f.key} className="block">
              <span className="text-xs text-zinc-500">{f.label}</span>
              <input
                type="password"
                className="fld mt-1 text-xs" placeholder="https://outlook.office.com/webhook/…"
                defaultValue={teams[f.key] || ""}
                readOnly={readOnly}
                onBlur={e => setTeams({ [f.key]: e.target.value })}
                data-testid={`int-${f.key}`}
              />
              <div className="font-mono text-[10px] text-zinc-400 mt-1">{f.hint}</div>
            </label>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-zinc-600">
            <input type="checkbox" checked={!!teams.enabled} onChange={e => setTeams({ enabled: e.target.checked })} data-testid="int-teams-enabled" />
            Enable Teams delivery
          </label>
          <button className="btn btn-secondary btn-sm" onClick={sendTest} disabled={sendingTest || !teams.enabled || !teams.steeringWebhook} data-testid="int-teams-test">
            {sendingTest ? <><Loader2 size={12} className="animate-spin"/> Sending…</> : <><TestTube2 size={12}/> Send test message</>}
          </button>
        </div>
      </div>

      {/* Escalation tiers */}
      <div className="card p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={14} className="text-amber-600"/>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">SLA-driven escalation tiers</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="border border-[var(--border)] rounded-sm p-3">
            <div className="flex items-center gap-2 mb-1 text-blue-700"><Clock size={12}/> <span className="font-medium text-sm">Tier 1 · Reminder</span></div>
            <label className="text-xs text-zinc-500 block">Days before due</label>
            <input type="number" className="fld mt-1" defaultValue={esc.tier1DaysBeforeDue ?? 2} readOnly={readOnly}
                   onBlur={e => setEsc({ tier1DaysBeforeDue: Number(e.target.value) || 0 })} data-testid="esc-tier1" />
            <div className="font-mono text-[10px] text-zinc-500 mt-2">Gentle nudge to owner only.</div>
          </div>
          <div className="border border-[var(--border)] rounded-sm p-3">
            <div className="flex items-center gap-2 mb-1 text-amber-700"><AlertTriangle size={12}/> <span className="font-medium text-sm">Tier 2 · Overdue</span></div>
            <label className="text-xs text-zinc-500 block">Days after due</label>
            <input type="number" className="fld mt-1" defaultValue={esc.tier2DaysAfterDue ?? 0} readOnly={readOnly}
                   onBlur={e => setEsc({ tier2DaysAfterDue: Number(e.target.value) || 0 })} data-testid="esc-tier2" />
            <div className="font-mono text-[10px] text-zinc-500 mt-2">Owner + workstream lead. Dashboard flag.</div>
          </div>
          <div className="border border-[var(--border)] rounded-sm p-3">
            <div className="flex items-center gap-2 mb-1 text-red-700"><ShieldAlert size={12}/> <span className="font-medium text-sm">Tier 3 · Escalation</span></div>
            <label className="text-xs text-zinc-500 block">Business days overdue</label>
            <input type="number" className="fld mt-1" defaultValue={esc.tier3DaysAfterDue ?? 5} readOnly={readOnly}
                   onBlur={e => setEsc({ tier3DaysAfterDue: Number(e.target.value) || 0 })} data-testid="esc-tier3" />
            <div className="font-mono text-[10px] text-zinc-500 mt-2">Steering committee + full context.</div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-600">
          <input type="checkbox" checked={!!esc.severityAutoEscalate} onChange={e => setEsc({ severityAutoEscalate: e.target.checked })} data-testid="esc-severity-auto" />
          Critical severity items skip straight to Tier 3
        </label>
      </div>

      {/* On-call routing (RACI-driven) */}
      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-2">On-call routing · @mentions per workstream</div>
        <EditableTable
          sectionKey="integrations"
          listKey="onCall"
          testidPrefix="oc"
          labelSingular="workstream"
          columns={[
            { key: "workstream", label: "Workstream", width: "180px" },
            { key: "primary", label: "Primary owner", width: "180px" },
            { key: "primaryHandle", label: "@mention handle", width: "180px" },
            { key: "backup", label: "Backup" },
            { key: "manager", label: "Workstream manager" },
          ]}
          emptyRow={() => ({ workstream: "", primary: "", primaryHandle: "", backup: "", manager: "" })}
        />
      </div>

      {/* Scan + Dispatch */}
      <div className="card p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Notification scan</div>
            <div className="text-xs text-zinc-500 mt-1">Walks tasks, RAID, actions, defects, and cutover for items breaching the tier thresholds — no messages are sent until you click Dispatch.</div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button className="btn btn-secondary btn-sm" onClick={scan} disabled={previewing || readOnly} data-testid="int-scan">
              {previewing ? <><Loader2 size={12} className="animate-spin"/> Scanning…</> : <>Scan</>}
            </button>
            <button className="btn btn-primary btn-sm" onClick={dispatch} disabled={!preview || readOnly} data-testid="int-dispatch">
              <Send size={12}/> Dispatch
            </button>
          </div>
        </div>
        {preview && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            {[
              { title: "Tier 1 · Reminders", items: preview.tier1, color: "text-blue-700" },
              { title: "Tier 2 · Overdue",    items: preview.tier2, color: "text-amber-700" },
              { title: "Tier 3 · Escalations", items: preview.tier3, color: "text-red-700" },
            ].map(g => (
              <div key={g.title} className="border border-[var(--border)] rounded-sm p-3">
                <div className={`font-mono text-[10px] uppercase tracking-[0.14em] ${g.color} mb-2`}>{g.title} · {g.items.length}</div>
                <ul className="space-y-1 text-xs">
                  {g.items.slice(0, 8).map((it, i) => (
                    <li key={i} className="truncate">
                      <span className="font-mono text-[10px] text-zinc-400 mr-1">{it.kind}</span>
                      {it.title || it.name || it.ref}
                      {it.owner && <span className="text-zinc-400"> · {it.owner}</span>}
                    </li>
                  ))}
                  {g.items.length > 8 && <li className="text-zinc-400 italic">+ {g.items.length - 8} more</li>}
                  {g.items.length === 0 && <li className="text-zinc-400 italic">Nothing to report</li>}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bot / slash-command hints */}
      <div className="card p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare size={14} className="text-violet-600"/>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Teams bot &amp; slash commands</div>
        </div>
        <div className="text-xs text-zinc-600 mb-3">Once the bot app is published to your Teams tenant, users can query Harmonizer directly without opening the platform. Currently the platform exposes the underlying endpoints; the Teams bot registration is a one-time IT task.</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-[var(--border)] rounded-sm p-3"><code className="font-mono text-[11px] text-zinc-900">/status</code><div className="text-[11px] text-zinc-500 mt-1">Live executive summary — health, budget, top risks.</div></div>
          <div className="border border-[var(--border)] rounded-sm p-3"><code className="font-mono text-[11px] text-zinc-900">/risks</code><div className="text-[11px] text-zinc-500 mt-1">Top 5 open risks by severity + owner.</div></div>
          <div className="border border-[var(--border)] rounded-sm p-3"><code className="font-mono text-[11px] text-zinc-900">/cutover</code><div className="text-[11px] text-zinc-500 mt-1">War-room state — tasks in flight, blockers, next checkpoint.</div></div>
        </div>
      </div>

      {/* Dispatch log */}
      {log.length > 0 && (
        <div className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-2">Recent notification log · last {Math.min(20, log.length)} events</div>
          <div className="card p-3">
            <div className="space-y-1.5 max-h-64 overflow-y-auto font-mono text-[11px]">
              {log.slice(-20).reverse().map((l, i) => (
                <div key={i} className="flex items-center gap-2 text-zinc-700">
                  <span className="text-zinc-400">{new Date(l.at).toLocaleString()}</span>
                  <span className={l.tier === 3 ? "text-red-700" : l.tier === 2 ? "text-amber-700" : "text-blue-700"}>T{l.tier}</span>
                  <span className="truncate">{l.message}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
